import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon, PrismMetricBlock } from '@mylighthouse/prism-ember';
import { t, tKey } from 'ember-intl';
import changeColorClass from 'lumen/helpers/change-color-class';
import type {
  TotalRevenueTrendItem,
  TotalRevenueTrendReference,
} from 'lumen/models';
import {
  type Metric,
  METRIC_PAR_TRANSLATIONS,
  METRIC_TRANSLATIONS,
} from 'lumen/utils/constants/total-revenue-constants';
import dateRangeDisplay from 'lumen/utils/dates/date-range-display';

interface MarketTotalRevenueTooltipSignature {
  Args: {
    aggregation: 'day' | 'week' | 'month';
    isIncompleteAggregation: boolean;
    isPerAvailableRoom: boolean;
    metric: Metric;
    trendItem: TotalRevenueTrendItem;
  };
}

export default class MarketTotalRevenueTooltip extends Component<MarketTotalRevenueTooltipSignature> {
  @service declare dates: Services['dates'];
  @service declare intl: Services['intl'];
  @service declare totalRevenue: Services['total-revenue'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get incompleteAggregationTranslationKey() {
    switch (this.args.aggregation) {
      case 'week':
        return tKey('general.incomplete_period_detail.week');
      case 'month':
        return tKey('general.incomplete_period_detail.month');
      default:
        return '';
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get metricTranslationKey() {
    const translationMappingToUse = this.args.isPerAvailableRoom
      ? METRIC_PAR_TRANSLATIONS
      : METRIC_TRANSLATIONS;

    return translationMappingToUse[this.args.metric];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipTitle() {
    if (this.args.aggregation === 'day') {
      const format = this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG;
      return this.args.trendItem.date.format(format);
    }

    const fromDate = this.args.trendItem.date;
    const toDate = this.args.trendItem.date.endOf(this.args.aggregation);
    return dateRangeDisplay(fromDate, toDate, this.intl);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getFormattedRelativeChange(item: TotalRevenueTrendReference) {
    const relativeChange = this.getRelativeChange(item);
    return this.totalRevenue.formatRelativeChange(relativeChange);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getFormattedValue(item: TotalRevenueTrendItem | TotalRevenueTrendReference) {
    const value = item[this.args.metric];
    return this.totalRevenue.formatValue(value);
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRelativeChange(item: TotalRevenueTrendReference) {
    const value = this.args.trendItem[this.args.metric];
    const referenceValue = item[this.args.metric];

    return this.totalRevenue.calculateRelativeChange(value, referenceValue);
  }

  <template>
    <div
      class="rounded-100 border-neutral-default bg-elevation-surface border border-solid shadow-neutral-200"
    >
      <div
        class="text-300--regular border-neutral-default border-b border-solid pt-200 pr-300 pb-200 pl-300 text-center"
      >
        {{this.tooltipTitle}}
      </div>
      <div class="flex flex-row justify-evenly gap-1000 pr-300 pl-300">
        <PrismMetricBlock>
          <:value>{{this.getFormattedValue @trendItem}}</:value>
          <:label>{{t this.metricTranslationKey}}</:label>
        </PrismMetricBlock>

        {{#each @trendItem.sortedReferenceValues as |referenceItem|}}
          {{#let
            (this.getRelativeChange referenceItem)
            (this.getFormattedRelativeChange referenceItem)
            as |relativeChange formattedRelativeChange|
          }}
            <PrismMetricBlock>
              <:value>
                {{this.getFormattedValue referenceItem}}
                <span
                  class="text-200--regular
                    {{changeColorClass (if relativeChange relativeChange 0)}}"
                >
                  ({{formattedRelativeChange}})
                </span>
              </:value>

              <:label>{{t
                  "general.on.period"
                  period=referenceItem.translatedReferenceType
                }}</:label>
            </PrismMetricBlock>
          {{/let}}
        {{/each}}
      </div>
      {{#if @isIncompleteAggregation}}
        <div
          class="border-neutral-default flex items-center gap-100 border-t border-solid p-200"
        >
          <PrismIcon @color="warning" @name="warning" @size="extra-small" />
          <span class="text-200--regular">
            {{t this.incompleteAggregationTranslationKey}}
            {{t "general.incomplete_period_extra"}}
          </span>
        </div>
      {{/if}}
    </div>
  </template>
}
